import React, { useRef, useState, useMemo, useCallback } from "react";
import { Modal, message, Space, Button } from "antd";
import { useTranslation } from "react-i18next";
import DynamicForm from "@/components/DynamicForm";
import type { FormInstance } from "antd";
import SelectSupplierModal from "@/components/supplier/SelectSupplierModal";
import type { SupplierUserSimpleListItem } from "@/types/apis";
import { fillEmployeeInfo } from "@/pages/supplier/SupplierProfile/utils/fillFormFields";
import { getEvaluationFormItems } from "./config/form";
import { TabsField } from "./config/tabs";
import { useSupplierSelectModal } from "./hooks/useSupplierSelectModal";
import { useMaterialStatusOptions } from "./hooks/useMaterialStatusOptions";
import { useEvaluationProcess } from "./hooks/useEvaluationProcess";
import { useEvaluationCategories } from "./hooks/useEvaluationCategories";
import { useProcessCode } from "./hooks/useProcessCode";
import {
  getAccessEvaluationByEditId,
  getAccessEvaluationById,
} from "@/apis/supplier";
import styles from "./config/style.module.less";

// ==================== 类型定义 ====================
interface EvaluationModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: EvaluationFormData) => void;
  recordData?: any; // 查看时的记录数据
  taskId?: number; // 任务ID，用于判断调用哪个接口
  isEdit?: boolean; // 是否为编辑模式（区分新增和编辑）
  submitting?: boolean; // 提交中状态
  currentPersonFlag?: boolean; // 用于控制"评估邀请"标签的显示隐藏
  isReassess?: boolean; // 是否为重新评估模式
}

interface EvaluationFormData {
  materialName: string;
  materialNumber: string;
  materialPurpose: string;
  assessmentType: string;
  assessmentDate: any;
  notes: string;
}

// ==================== 常量配置 ====================
// 表单配置
const FORM_CONFIG = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
  layout: "horizontal" as const,
  colon: false,
};

// ==================== 工具函数 ====================

/**
 * 使用正则表达式从JSON字符串中保留大整数的精度
 * 避免 JSON.parse 导致的精度丢失
 * @param jsonString JSON格式的字符串
 * @returns 处理后的对象（所有大整数保持为字符串）
 */
const parseJSONWithBigInt = (jsonString: string): any => {
  // 正则表达式：匹配 "id": 数字 或 "parentId": 数字 的模式
  // 将大整数转换为字符串，避免精度丢失
  const processedString = jsonString.replace(
    /"(id|parentId)"\s*:\s*(\d+)/g,
    (match, key, value) => {
      // 如果数字超过15位（安全整数范围），转换为字符串
      if (value.length > 15) {
        return `"${key}":"${value}"`;
      }
      return match;
    },
  );

  return JSON.parse(processedString);
};

/**
 * 确保evaluationData中所有id都保持字符串格式，避免JSON序列化导致的精度丢失
 * @param data 评估数据
 * @returns 处理后的评估数据
 */
const ensureStringIds = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map((item) => ensureStringIds(item));
  }

  if (data && typeof data === "object") {
    const result: any = {};
    Object.keys(data).forEach((key) => {
      if (key === "id" && data[key] !== null && data[key] !== undefined) {
        // id 字段强制转换为字符串，保留精度
        result[key] = String(data[key]);
      } else if (
        key === "parentId" &&
        data[key] !== null &&
        data[key] !== undefined
      ) {
        // parentId 字段也需要强制转换为字符串
        result[key] = String(data[key]);
      } else if (Array.isArray(data[key])) {
        // 递归处理数组
        result[key] = ensureStringIds(data[key]);
      } else if (data[key] && typeof data[key] === "object") {
        // 递归处理对象
        result[key] = ensureStringIds(data[key]);
      } else {
        result[key] = data[key];
      }
    });
    return result;
  }

  return data;
};

/**
 * 从后端数据转换为表单字段数据（API响应 → 表单字段）
 * 需要传入 evaluationData 来建立名称到ID的映射
 */
const transformAPIDataToForm = (backendData: any, evaluationData?: any) => {
  // 首先确保参数中的evaluationData也是字符串格式的ID
  if (evaluationData) {
    evaluationData = ensureStringIds(evaluationData);
  }

  const fieldMapping = {
    // 申请人信息字段映射
    purchaseUserNickname: "employeeName",
    purchaseUserUsername: "employeeCode",
    purchaseUserDeptName: "department",
    purchaseUserDeptNo: "deptNo",
    purchaseUserPostName: "position",
    purchaseUserPostNo: "postNo",
    purchaseUserEmail: "email",
    purchaseUserId: "purchaseUserId",
    // 供应商信息字段映射
    supplierUserName: "primaryCategory",
    supplierUsUserName: "primaryCategoryEn",
    supplierUserId: "supplierUserId", // 供应商ID
    supplierStatusName: "supplierStatus",
    materialStatusCode: "materialStatus", // 修复:使用物料状态编码而非名称
    materialCategoryName: "materialUsageType",
    materialNumber: "materialCode",
    materialName: "materialName",
    registrationMarketName: "businessRegistration",
    productionRegion: "productionArea",
    // 其他字段
    createDate: "createDate",
  };

  const formData: Record<string, any> = {};

  // 映射普通字段
  Object.entries(fieldMapping).forEach(([backendKey, formKey]) => {
    if (backendKey in backendData) {
      formData[formKey] = backendData[backendKey];
    }
  });

  // 处理评估数据（如果存在）
  // 优先使用已经处理过的evaluationData参数（已保证ID为字符串格式）
  let processedEvaluationData = evaluationData;
  if (!evaluationData && backendData.evaluationData) {
    // 只有在没有传入参数时才重新解析后端数据
    try {
      let parsedData =
        typeof backendData.evaluationData === "string"
          ? parseJSONWithBigInt(backendData.evaluationData) // 使用正则表达式处理大整数
          : backendData.evaluationData;
      // 确保所有id都是字符串格式，避免JSON.parse导致的精度丢失
      parsedData = ensureStringIds(parsedData);
      processedEvaluationData = parsedData;
    } catch (error) {}
  }

  // 将处理后的数据添加到formData
  if (processedEvaluationData) {
    formData.evaluationData = processedEvaluationData;
  }

  // 建立名称到ID的映射表（用于处理 evaluationTemplateNames）
  const nameToIdMap = new Map<string, string>();
  const buildNameToIdMap = (nodes: any[]) => {
    nodes.forEach((node: any) => {
      if (node.id && node.name) {
        nameToIdMap.set(node.name, node.id);
      }
      if (node.childList && node.childList.length > 0) {
        buildNameToIdMap(node.childList);
      }
    });
  };
  if (processedEvaluationData && Array.isArray(processedEvaluationData)) {
    buildNameToIdMap(processedEvaluationData);
  }

  // 处理评估邀请数据
  if (
    backendData.srmSupplierAccessEvaluationInvitationVOS &&
    backendData.srmSupplierAccessEvaluationInvitationVOS.length > 0
  ) {
    formData.assessmentInviteItems =
      backendData.srmSupplierAccessEvaluationInvitationVOS.map((item: any) => {
        let evaluationTemplateIds: string[] = [];

        // 优先使用 evaluationTemplateIds（如果有的话）
        if (item.evaluationTemplateIds) {
          let ids = item.evaluationTemplateIds;

          // 如果是JSON字符串格式，解析为数组（避免精度丢失）
          if (typeof ids === "string" && ids.trim().startsWith("[")) {
            try {
              // 使用正则表达式提取数字字符串，避免 JSON.parse 导致的精度丢失
              // 匹配模式：数字序列（包含逗号分隔）
              const numberMatches = ids.match(/\d+/g);
              ids = numberMatches || [];
            } catch (error) {
              ids = [];
            }
          } else if (typeof ids === "string" && ids) {
            // 如果是逗号分隔的普通字符串，分割为数组
            ids = ids
              .split(",")
              .map((id) => id.trim())
              .filter((id) => id);
          }

          // 确保所有元素都是字符串且去掉空值
          evaluationTemplateIds = Array.isArray(ids)
            ? ids.map((id: any) => String(id).trim()).filter((id) => id)
            : [];
        }
        // 如果没有 evaluationTemplateIds，尝试从 evaluationTemplateNames 反查
        else if (item.evaluationTemplateNames) {
          let names: string[] = [];

          // 解析 evaluationTemplateNames（JSON 字符串格式）
          if (
            typeof item.evaluationTemplateNames === "string" &&
            item.evaluationTemplateNames.startsWith("[")
          ) {
            try {
              names = JSON.parse(item.evaluationTemplateNames);
            } catch (error) {
              names = [];
            }
          } else if (
            typeof item.evaluationTemplateNames === "string" &&
            item.evaluationTemplateNames
          ) {
            names = [item.evaluationTemplateNames];
          } else if (Array.isArray(item.evaluationTemplateNames)) {
            names = item.evaluationTemplateNames;
          }

          // 根据名称反查ID
          evaluationTemplateIds = names
            .map((name: string) => nameToIdMap.get(name) || "")
            .filter((id: string) => id !== "");
        }

        return {
          id: item.id || "", // 邀请记录ID（用于编辑/删除）
          assessmentPhase: evaluationTemplateIds, // 评估阶段ID数组
          assessmentPerson: item.purchaseUserId || "", // 评估人ID
          assessmentPersonName: item.purchaseUserName || "", // 评估人名称
          evaluationStatus: item.evaluationStatus || null, // 评估邀请状态 (1: 评估中, 2: 已评估, null: 待发送)
        };
      });
  } else {
    // 如果评估邀请数据为空，初始化为一个空行
    formData.assessmentInviteItems = [
      {
        assessmentPhase: [],
        assessmentPerson: "",
      },
    ];
  }

  return formData;
};

// ==================== 渲染函数 ====================
const getModalTitle = (processCode: string, t: any) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        paddingLeft: "24px",
      }}
    >
      <span>{t("供应商准入评估表")}</span>
      <div
        style={{
          width: "2px",
          height: "20px",
          backgroundColor: "#d9d9d9",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: "14px", color: "rgba(0, 0, 0, 0.45)" }}>
        {t("单号")}：{processCode || t("加载中...")}
      </span>
    </div>
  );
};

const getModalFooter = (
  handleSubmit: () => void,
  handleSave: () => void,
  submitting: boolean = false,
  isEdit: boolean = false,
  t: any,
) => (
  <div className={styles.modalFooterContainer}>
    <Space>
      {!isEdit && (
        <Button className={styles.saveButton} onClick={handleSave}>
          {t("暂存")}
        </Button>
      )}
      <Button
        type="primary"
        className={styles.submitButton}
        onClick={handleSubmit}
        loading={submitting}
      >
        {t("提交")}
      </Button>
    </Space>
  </div>
);

// ==================== 主组件 ====================
const AssessmentModal: React.FC<EvaluationModalProps> = ({
  visible,
  onClose,
  onSubmit,
  recordData,
  taskId,
  isEdit = false,
  submitting = false,
  currentPersonFlag = false,
  isReassess = false,
}) => {
  const { t } = useTranslation("evaluationTab");
  const formRef = useRef<FormInstance>(null);
  const tabsFieldRef = useRef<{
    getEvaluationData: () => any[];
    getRemarksData: () => Record<string, Record<string, string>>;
    clearAssessmentData: () => void;
  }>(null);
  const [loading, setLoading] = useState(false);
  const [selectedMaterialStatus, setSelectedMaterialStatus] =
    useState<string>();
  const [editModeEvaluationData, setEditModeEvaluationData] =
    useState<any>(null); // 编辑模式下的评估数据
  const [originalEvaluationStatus, setOriginalEvaluationStatus] = useState<
    number | null
  >(null); // 存储接口返回的原始 evaluationStatus

  // 使用Hook获取流程单号
  const {
    processCode: fetchedProcessCode,
    loading: processCodeLoading,
    fetchProcessCode,
  } = useProcessCode();

  // 维护 processCode state：编辑模式下从后端数据获取，新增模式下从接口获取
  const [processCode, setProcessCode] = useState<string>("");

  // 使用Hook管理供应商选择弹框
  const {
    modalOpen: supplierModalOpen,
    handleOpenModal: handleOpenSupplierModal,
    handleCloseModal: handleCloseSupplierModal,
  } = useSupplierSelectModal();

  // 使用Hook获取物料状态选项（仅在弹窗打开时获取，但在只读模式下不获取）
  const { options: materialStatusOptions } = useMaterialStatusOptions(
    visible && !isEdit,
  );

  // 使用Hook管理评估流程数据
  const {
    processList,
    loading: processLoading,
    fetchEvaluationProcess,
    clearProcessList,
  } = useEvaluationProcess();

  // 使用Hook获取评估分类数据（基于选中的物料状态）
  const { options: evaluationCategoryOptions } = useEvaluationCategories(
    selectedMaterialStatus,
  );

  // 从 processList 或 editModeEvaluationData 中提取评估阶段选项作为补充
  // 用于编辑模式下确保下拉选项包含后端返回的评估阶段ID
  const supplementaryEvaluationOptions = useMemo(() => {
    const extractStageOptions = (
      nodes: any[],
    ): { label: string; value: string }[] => {
      const options: { label: string; value: string }[] = [];

      const traverse = (nodeList: any[]) => {
        nodeList.forEach((node: any) => {
          if (node.nodeType === "STAGE" && node.id && node.name) {
            options.push({
              label: node.name,
              value: String(node.id), // 确保ID为字符串格式
            });
          }
          if (node.childList && node.childList.length > 0) {
            traverse(node.childList);
          }
        });
      };

      traverse(nodes);
      return options;
    };

    // 优先从 editModeEvaluationData 提取（编辑模式）
    if (editModeEvaluationData && Array.isArray(editModeEvaluationData)) {
      return extractStageOptions(editModeEvaluationData);
    }

    // 其次从 processList 提取（新增模式或编辑模式备选）
    if (processList && Array.isArray(processList)) {
      return extractStageOptions(processList);
    }

    return [];
  }, [editModeEvaluationData, processList]);

  // 合并评估分类选项：优先使用 Hook 获取的选项，补充从数据中提取的选项
  const mergedEvaluationOptions = useMemo(() => {
    const optionsMap = new Map<string, { label: string; value: string }>();

    // 先添加从数据中提取的选项（作为基础）
    supplementaryEvaluationOptions.forEach((option) => {
      optionsMap.set(option.value, option);
    });

    // 再添加 Hook 获取的选项（可能覆盖或补充）
    evaluationCategoryOptions.forEach((option: any) => {
      if (option.value && option.label) {
        optionsMap.set(option.value, option);
      }
    });

    return Array.from(optionsMap.values());
  }, [evaluationCategoryOptions, supplementaryEvaluationOptions]);

  // 处理供应商选择
  const handleSupplierSelect = useCallback(
    (supplier: SupplierUserSimpleListItem | undefined) => {
      if (supplier) {
        // 填充中文和英文供应商名称
        formRef.current?.setFieldValue(
          "primaryCategory",
          supplier.supplierName || "",
        );
        formRef.current?.setFieldValue(
          "primaryCategoryEn",
          supplier.supplierUsName || "",
        );
        // 保存供应商ID
        formRef.current?.setFieldValue("supplierUserId", supplier.id || "");
      }
      handleCloseSupplierModal(supplier);
    },
    [handleCloseSupplierModal],
  );

  const formItems = useMemo(
    () =>
      getEvaluationFormItems(
        t,
        materialStatusOptions,
        processList,
        handleOpenSupplierModal,
      ),
    [t, materialStatusOptions, processList, handleOpenSupplierModal],
  );
  const modalTitle = getModalTitle(processCode, t);

  // 当弹框打开或关闭时，处理表单和下拉数据的初始化和清空
  React.useEffect(() => {
    if (visible) {
      // 只有在新增模式下才调取流程单号接口
      if (!isEdit) {
        fetchProcessCode();
      }

      // 如果是编辑模式，调取详情接口
      if (isEdit && recordData?.id) {
        setLoading(true);

        // 根据 taskId 是否存在决定调用哪个接口
        // taskId 不存在时调用 editInfoById（编辑接口）
        // taskId 存在时调用 getInfoById（查看接口）
        const shouldUseEditApi = !taskId;

        const fetchDetailApi = shouldUseEditApi
          ? getAccessEvaluationByEditId(recordData.id)
          : getAccessEvaluationById(recordData.id);

        fetchDetailApi
          .then((response: any) => {
            if (response.data?.code === 200) {
              const detailData = response.data?.data || {};

              // 当从工作台点击且有 taskId 时，标记为来自GM
              if (taskId) {
                detailData.isFromGm = true;
              }

              // 编辑模式下从后端数据获取 processCode
              if (detailData.processCode) {
                setProcessCode(detailData.processCode);
              }

              // 保存接口返回的原始 evaluationStatus
              if (
                detailData.evaluationStatus !== undefined &&
                detailData.evaluationStatus !== null
              ) {
                setOriginalEvaluationStatus(detailData.evaluationStatus);
              }

              // 预处理评估数据以便建立映射关系
              let evaluationDataForMapping: any = undefined;
              if (detailData.evaluationData) {
                try {
                  evaluationDataForMapping =
                    typeof detailData.evaluationData === "string"
                      ? parseJSONWithBigInt(detailData.evaluationData) // 使用正则表达式保留精度
                      : detailData.evaluationData;
                  // 确保所有id都是字符串格式，避免精度丢失
                  evaluationDataForMapping = ensureStringIds(
                    evaluationDataForMapping,
                  );
                } catch (error) {}
              }

              // 使用转换函数将后端数据转换为表单字段
              const formData = transformAPIDataToForm(
                detailData,
                evaluationDataForMapping,
              );

              // 编辑模式下，确保createDate字段被正确设置
              // 检查多个可能的字段名
              const createDateValue =
                formData.createDate ||
                detailData.createDate ||
                detailData.gmtCreate ||
                detailData.createdTime ||
                detailData.createTime;

              if (createDateValue) {
                formData.createDate = createDateValue;
              }

              // 填充表单字段
              formRef.current?.setFieldsValue(formData);

              // 如果有评估数据，初始化评估表格的选择状态
              if (
                formData.evaluationData &&
                tabsFieldRef.current?.initializeEvaluationData
              ) {
                // 编辑模式：保存原始评估数据，作为表格的数据源
                setEditModeEvaluationData(formData.evaluationData);
                tabsFieldRef.current.initializeEvaluationData(
                  formData.evaluationData,
                );
              }
              // 如果有物料状态，设置选中状态（不调取接口，编辑模式直接使用后端数据）

              if (formData.materialStatus) {
                setSelectedMaterialStatus(formData.materialStatus);
              }
            } else {
              message.error(response.data?.message || "获取评估详情失败");
            }
          })
          .catch((error: any) => {
            message.error("获取评估详情失败，请重试");
          })
          .finally(() => {
            setLoading(false);
          });
      } else if (recordData) {
        // 非编辑模式但有记录数据，直接转换填充
        // 预处理评估数据以建立映射关系
        let evaluationDataForMapping: any = undefined;
        if (recordData.evaluationData) {
          try {
            evaluationDataForMapping =
              typeof recordData.evaluationData === "string"
                ? parseJSONWithBigInt(recordData.evaluationData) // 使用正则表达式保留精度
                : recordData.evaluationData;
            // 确保所有id都是字符串格式，避免精度丢失
            evaluationDataForMapping = ensureStringIds(
              evaluationDataForMapping,
            );
          } catch (error) {}
        }

        const formData = transformAPIDataToForm(
          recordData,
          evaluationDataForMapping,
        );
        formRef.current?.setFieldsValue(formData);

        // 如果有评估数据，初始化评估表格的选择状态
        if (
          formData.evaluationData &&
          tabsFieldRef.current?.initializeEvaluationData
        ) {
          tabsFieldRef.current.initializeEvaluationData(
            formData.evaluationData,
          );
        }
      } else {
        // 新增模式，填充申请人信息
        fillEmployeeInfo(formRef);
        // 新增模式下，评估邀请项初始化为一个空行
        formRef.current?.setFieldsValue({
          assessmentInviteItems: [
            {
              assessmentPhase: [],
              assessmentPerson: "",
            },
          ],
        });
      }
    } else {
      // 弹框关闭时，清空所有缓存数据
      setProcessCode(""); // 清空 processCode
      setSelectedMaterialStatus(undefined);
      setOriginalEvaluationStatus(null); // 清空原始 evaluationStatus
      setEditModeEvaluationData(null); // 清空编辑模式的评估数据
      clearProcessList(); // 清空评估流程数据
      tabsFieldRef.current?.clearAssessmentData();
      formRef.current?.resetFields();
    }
  }, [
    visible,
    recordData,
    isEdit,
    isReassess,
    taskId,
    clearProcessList,
    fetchProcessCode,
    fetchEvaluationProcess,
  ]);

  // 监听接口返回的 fetchedProcessCode，并同步到 processCode state（新增模式）
  React.useEffect(() => {
    if (fetchedProcessCode) {
      setProcessCode(fetchedProcessCode);
    }
  }, [fetchedProcessCode]);

  // 监听物料状态字段变化
  const handleFormValuesChange = React.useCallback(
    (changedValues: any, allValues: any) => {
      // 当物料状态（materialStatus）字段变化时
      if ("materialStatus" in changedValues) {
        const materialStatusId = changedValues.materialStatus;

        if (materialStatusId) {
          // 更新选中的物料状态，触发 useEvaluationCategories hook 获取评估分类数据
          setSelectedMaterialStatus(materialStatusId);
          // 用户修改物料状态时，清空编辑模式的评估数据，使用新获取的数据
          setEditModeEvaluationData(null);

          // 从表单中获取部门编号
          const deptId = allValues.deptNo || "";

          if (deptId) {
            // 调取接口获取评估流程
            fetchEvaluationProcess({
              materialTypeId: materialStatusId,
              deptId: deptId,
            });
          } else {
            message.warning("无法获取部门信息，请联系管理员");
          }
        } else {
          // 清空选中的物料状态
          setSelectedMaterialStatus(undefined);
          setEditModeEvaluationData(null);
        }
      }
    },
    [fetchEvaluationProcess],
  );

  // ==================== 字段映射转换 ====================
  /**
   * 将表单字段名转换为后端API所需的字段名
   * @param formData 表单数据
   * @param isEditMode 是否为编辑模式，编辑模式下需要保留createDate字段
   */
  const transformFormDataToAPI = (
    formData: any,
    isEditMode: boolean = false,
  ) => {
    const fieldMapping = {
      // 申请人信息
      employeeName: "purchaseUserNickname",
      employeeCode: "purchaseUserUsername",
      department: "purchaseUserDeptName",
      deptNo: "purchaseUserDeptNo",
      position: "purchaseUserPostName",
      postNo: "purchaseUserPostNo",
      email: "purchaseUserEmail",
      purchaseUserId: "purchaseUserId",
      // 供应商信息
      primaryCategory: "supplierUserName",
      primaryCategoryEn: "supplierUsUserName",
      supplierUserId: "supplierUserId", // 供应商ID
      supplierStatus: "supplierStatusName",
      materialStatus: "materialStatusCode", // 表单的 materialStatus 映射为 API 的 materialStatusCode
      materialUsageType: "materialCategoryName",
      materialCode: "materialNumber",
      materialName: "materialName",
      businessRegistration: "registrationMarketName",
      productionArea: "productionRegion",
    };

    // 需要排除的字段列表（编辑模式下需要保留createDate）
    const excludedFields = isEditMode ? [] : ["createDate"];

    // 构建转换后的数据对象
    const transformedData: Record<string, any> = {};

    Object.keys(formData).forEach((key) => {
      // 跳过被排除的字段
      if (excludedFields.includes(key)) {
        return;
      }

      const mappedKey = fieldMapping[key as keyof typeof fieldMapping] || key;
      transformedData[mappedKey] = formData[key];
    });

    // 特殊处理：如果有 materialStatus（物料状态代码），还需要补充 materialStatusName（物料状态名称）
    // 从 materialStatusOptions 中查找对应的名称
    if (formData.materialStatus && materialStatusOptions.length > 0) {
      const selectedOption = materialStatusOptions.find(
        (opt: any) => opt.value === formData.materialStatus,
      );
      if (selectedOption) {
        transformedData.materialStatusName = selectedOption.label;
      }
    }

    return transformedData;
  };

  /**
   * 从evaluationData中提取所有nodeType=STAGE的id作为evaluationTemplateIds数组
   * @param data 评估数据数组
   * @returns STAGE节点的id数组，确保是字符串格式
   */
  const extractEvaluationTemplateIds = (data: any[]): string[] => {
    const stageIds: string[] = [];

    const traverse = (nodes: any[]) => {
      nodes.forEach((node) => {
        if (node.nodeType === "STAGE") {
          // 确保返回字符串格式，保留精度
          const id = String(node.id || "");
          if (id && !stageIds.includes(id)) {
            stageIds.push(id);
          }
        }
        // 递归查找子节点中的STAGE类型
        if (node.childList && node.childList.length > 0) {
          traverse(node.childList);
        }
      });
    };

    traverse(data);
    return stageIds;
  };

  /**
   * 构建提交数据的通用逻辑
   * @param evaluationStatus 评估状态：0=草稿，1=已提交（有邀请）
   * @param isSubmitting 是否为提交操作（true时邀请项evaluationStatus统一为1，false时保留原值或使用0）
   * @param originalStatus 原始的evaluationStatus值（用于判断是否需要转换）
   */
  const buildSubmitData = async (
    evaluationStatus: number,
    isSubmitting: boolean = false,
    originalStatus: number | null = null,
  ): Promise<any | null> => {
    try {
      const values = await formRef.current?.validateFields();
      if (!values) return null;

      // 获取评估数据和remarks数据
      const evaluationData = tabsFieldRef.current?.getEvaluationData() || [];
      const remarksData = tabsFieldRef.current?.getRemarksData() || {};

      // 遍历evaluationData，将remarks数据合并到对应的levelList项中
      const mergeRemarksToEvaluationData = (data: any[]): any[] => {
        return data.map((node: any) => {
          // 如果该节点有levelList，更新其中被checked的项的remark
          if (node.levelList && node.levelList.length > 0) {
            const nodeId = node.id;
            // 在RemarksData中找到对应的remarks（按节点ID或其他标识）
            // 注意：这里需要根据实际的数据结构来调整
            node.levelList = node.levelList.map((level: any) => {
              if (level.checked) {
                // 从remarksData中查找对应的remark值
                // remarksData的结构是 { [tableKey]: { [recordKey]: remarksValue } }
                let remarkValue = "";
                Object.values(remarksData).forEach((tableRemarks: any) => {
                  if (tableRemarks[nodeId]) {
                    remarkValue = tableRemarks[nodeId];
                  }
                });
                return {
                  ...level,
                  remark: remarkValue || level.remark || "",
                };
              }
              return level;
            });
          }

          // 递归处理子节点
          if (node.childList && node.childList.length > 0) {
            node.childList = mergeRemarksToEvaluationData(node.childList);
          }

          return node;
        });
      };

      const mergedEvaluationData = mergeRemarksToEvaluationData(evaluationData);

      // 确保evaluationData中所有id都是字符串格式，避免JSON序列化时精度丢失
      const evaluationDataWithStringIds = ensureStringIds(mergedEvaluationData);

      // 转换字段名（编辑模式下保留createDate字段）
      const transformedData = transformFormDataToAPI(values, isEdit);

      // 从evaluationData中提取evaluationTemplateIds（所有STAGE节点的id数组），确保是字符串
      const evaluationTemplateIds =
        extractEvaluationTemplateIds(evaluationData);

      // 处理评估邀请数据
      const assessmentInviteItems = values.assessmentInviteItems || [];

      // 构建评估阶段id到name的映射表
      const evaluationTemplateMap = new Map<string, string>();

      // 从 processList/evaluationData 中提取映射
      const buildTemplateMap = (nodes: any[]) => {
        nodes.forEach((node: any) => {
          if (node.id && node.name) {
            // 确保 key 和 value 都是字符串格式，同时保留数字格式的 key 用于兼容
            const stringId = String(node.id);
            evaluationTemplateMap.set(stringId, node.name);
            // 如果原始id是数字，也添加数字格式的映射（兼容处理）
            if (typeof node.id === "number" || !isNaN(Number(node.id))) {
              evaluationTemplateMap.set(node.id, node.name);
            }
          }
          if (node.childList && node.childList.length > 0) {
            buildTemplateMap(node.childList);
          }
        });
      };

      // 新增模式优先使用 processList（完整的模板结构），编辑模式优先使用 editModeEvaluationData（后端返回的完整数据）
      // 优化数据源选择逻辑：确保数据源数组有效
      let dataSourceForMap = isEdit
        ? editModeEvaluationData || evaluationData || processList
        : processList || evaluationData;

      // 如果主数据源为空或不是数组，尝试使用其他数据源
      if (!Array.isArray(dataSourceForMap) || dataSourceForMap.length === 0) {
        if (Array.isArray(processList) && processList.length > 0) {
          dataSourceForMap = processList;
        } else if (Array.isArray(evaluationData) && evaluationData.length > 0) {
          dataSourceForMap = evaluationData;
        } else if (
          Array.isArray(editModeEvaluationData) &&
          editModeEvaluationData.length > 0
        ) {
          dataSourceForMap = editModeEvaluationData;
        }
      }

      // 确保数据源是有效数组后再构建映射表
      if (Array.isArray(dataSourceForMap)) {
        buildTemplateMap(dataSourceForMap);
      } else {
      }

      // 从 evaluationCategoryOptions 中也提取映射（以防数据不完整）
      if (Array.isArray(evaluationCategoryOptions)) {
        evaluationCategoryOptions.forEach((option: any) => {
          if (
            option.value &&
            option.label &&
            !evaluationTemplateMap.has(option.value)
          ) {
            evaluationTemplateMap.set(option.value, option.label);
          }
        });
      }
      const filteredAssessmentInviteItems = assessmentInviteItems.filter(
        (item: any) => {
          // 检查 assessmentPhase 是否有值（支持数组或单个值）
          const hasPhase = Array.isArray(item.assessmentPhase)
            ? item.assessmentPhase.length > 0
            : !!item.assessmentPhase;
          // 检查 assessmentPerson 或 assessmentPersonName 是否有值
          const hasPerson = item.assessmentPerson || item.assessmentPersonName;
          return hasPhase && hasPerson;
        },
      );

      // const hasSuccessfulInvitation = filteredAssessmentInviteItems.some(
      //   (item: any) => item.isSendInvite === true,
      // );

      const srmSupplierAccessEvaluationInvitations =
        filteredAssessmentInviteItems.map((item: any, index: number) => {
          // 处理evaluationTemplateIds：支持单个值或数组
          const assessmentPhases = Array.isArray(item.assessmentPhase)
            ? item.assessmentPhase
            : item.assessmentPhase
              ? [item.assessmentPhase]
              : [];

          // 根据id数组获取对应的名称数组，确保类型匹配
          const evaluationTemplateNames = assessmentPhases.map((id: string) => {
            // 同时尝试字符串格式和原始格式查找
            const stringId = String(id);
            const name =
              evaluationTemplateMap.get(stringId) ||
              evaluationTemplateMap.get(id);
            return name || "";
          });

          const invitationData: any = {
            purchaseUserId: item.assessmentPerson || "", // 可能为空，但有名称
            purchaseUserName: item.assessmentPersonName || "", // 评估人名称
            evaluationTemplateIds: assessmentPhases, // 评估阶段ID数组
            evaluationTemplateNames: evaluationTemplateNames, // 评估阶段名称数组
            evaluationStatus: isSubmitting ? 1 : (item.evaluationStatus ?? 0), // 提交时传1，暂存时保留原值或使用0
            isSendInvite: item.isSendInvite ?? false, // 如果未定义，默认为 false
          };

          // 编辑模式下，添加id字段
          if (isEdit && item.id) {
            invitationData.id = item.id;
          }

          return invitationData;
        });

      // 移除 assessmentInviteItems 字段，构建最终提交数据
      const { assessmentInviteItems: _, ...dataWithoutInviteItems } =
        transformedData;

      // 根据原始evaluationStatus动态转换提交的evaluationStatus
      let finalEvaluationStatus = evaluationStatus;
      if (isSubmitting && isEdit && originalStatus === 1) {
        // 编辑模式下，如果原始状态是1，提交时转换为2
        finalEvaluationStatus = 2;
      }
      // 重新评估模式下，重置为1（已提交状态）
      if (isReassess && isSubmitting) {
        finalEvaluationStatus = 1;
      }

      const submitData = {
        ...(isEdit && recordData?.id && !isReassess
          ? { id: recordData.id }
          : {}), // 编辑模式下添加id字段，重新评估模式除外
        processCode: processCode, // 添加流程单号
        ...dataWithoutInviteItems,
        evaluationStatus: finalEvaluationStatus, // 使用转换后的状态值
        evaluationTemplateIds, // 添加从evaluationData提取的evaluationTemplateIds数组
        srmSupplierAccessEvaluationInvitations,
        evaluationData: JSON.stringify(evaluationDataWithStringIds), // 使用处理后的数据，确保所有id都是字符串
      };
      return submitData;
    } catch (error) {
      return null;
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // 暂存时，evaluationStatus 设为 0（草稿），邀请项evaluationStatus保留原值或使用0
      const submitData = await buildSubmitData(
        0,
        false,
        originalEvaluationStatus,
      );
      if (submitData) {
        onSubmit(submitData);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      // 提交时，evaluationStatus 统一传1
      const values = await formRef.current?.validateFields();
      if (!values) return;

      // 提交时，evaluationStatus 设为 1（表示已提交），邀请项evaluationStatus也统一为1
      const submitData = await buildSubmitData(
        1,
        true,
        originalEvaluationStatus,
      );
      if (submitData) {
        onSubmit(submitData);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // 清空评估分类选项（通过重置物料状态）
    setSelectedMaterialStatus(undefined);
    // 清空评估流程数据
    clearProcessList();
    // 清空表格中的评估数据
    tabsFieldRef.current?.clearAssessmentData();
    // 重置表单
    formRef.current?.resetFields();
    // 关闭弹框
    onClose();
  };

  return (
    <>
      <Modal
        title={modalTitle}
        open={visible}
        confirmLoading={loading || processLoading || processCodeLoading}
        width="1300px"
        centered
        style={{ top: 10, paddingBottom: 20 }}
        classNames={{
          header: styles.modalHeader,
          body: styles.modalBody,
        }}
        footer={() =>
          getModalFooter(handleSubmit, handleSave, submitting, isEdit, t)
        }
        onCancel={handleCancel}
      >
        <DynamicForm
          ref={formRef}
          formItem={formItems.map((item) =>
            item.name === "assessmentTabs"
              ? {
                  ...item,
                  custom: {
                    render: () => (
                      <TabsField
                        ref={tabsFieldRef}
                        backendData={editModeEvaluationData || processList}
                        evaluationCategoryOptions={mergedEvaluationOptions}
                        editId={isEdit ? recordData?.id : undefined}
                        isEdit={isEdit}
                        currentPersonFlag={currentPersonFlag}
                      />
                    ),
                  },
                }
              : item,
          )}
          formConfig={FORM_CONFIG}
          onValuesChange={handleFormValuesChange}
        />
      </Modal>

      {/* 供应商选择弹框 */}
      <SelectSupplierModal
        open={supplierModalOpen}
        onClose={handleSupplierSelect}
      />
    </>
  );
};

export default AssessmentModal;
